import { useMemo, useCallback, useState, useEffect } from "react";
import { sendGTMEvent } from "@next/third-parties/google";
import { useCarFilters } from "./useCarFilters";
import { useURLArrayPatch, useURLDeleteKeys } from "./useURLState";
import type { SearchSideProps, SelectOption, RangeValue } from "@/types/searchSide";
export const useOptimizedFilters = (props: SearchSideProps) => {
  const [expandedMakes, setExpandedMakes] = useState<string[]>([]);

  // Memoize range bounds calculation from facets
  const rangeBounds = useMemo(() => ({
    minYear: props.facets?.year?.[0] ?? 2000,
    maxYear: props.facets?.year?.[1] ?? 2025,
    minMileage: props.facets?.mileage?.[0] ?? 0,
    maxMileage: props.facets?.mileage?.[1] ?? 200000,
    minPrice: props.facets?.price?.[0] ?? 0,
    maxPrice: props.facets?.price?.[1] ?? 100000
  }), [props.facets]);
  const hooks = useCarFilters(rangeBounds);

  // Transform facets make data to SelectOption format
  const makesData = useMemo(() => {
    if (props.facets?.make) {
      return props.facets.make.map(facet => ({
        value: facet.value,
        label: facet.label || `${facet.value} (${facet.count})`
      }));
    }
    return [];
  }, [props.facets?.make]);

  // Auto-expand makes when they are selected
  useEffect(() => {
    if (hooks.selectedMakes.length > 0) {
      setExpandedMakes(prev => {
        const newExpanded = [...prev];
        hooks.selectedMakes.forEach(make => {
          if (!newExpanded.includes(make)) {
            newExpanded.push(make);
          }
        });
        return newExpanded;
      });
    }
  }, [hooks.selectedMakes]);

  // Clear models when makes are cleared
  const handleMakeChange = useCallback((selectedMakes: string[]) => {
    if (selectedMakes.length === 0) {
      hooks.setSelectedModels([]);
    }
    hooks.setSelectedMakes(selectedMakes);
  }, [hooks]);
  const [patchMakeModels] = useURLArrayPatch(["page"]);
  const handleMakeModelChange = useCallback((makes: string[], models: string[]) => {
    patchMakeModels({
      make: makes,
      model: models
    });
  }, [patchMakeModels]);
  const handleModelChange = useCallback((selectedModels: string[]) => {
    hooks.setSelectedModels(selectedModels);
  }, [hooks]);
  const handleClearMakes = useCallback(() => {
    hooks.setSelectedMakes([]);
    hooks.setSelectedModels([]);
  }, [hooks]);
  const handleClearModels = useCallback(() => {
    hooks.setSelectedModels([]);
  }, [hooks]);

  // Atomic clear for both make and model query params to avoid race conditions
  const [clearMakeModelQuery] = useURLDeleteKeys(["make", "model", "page"]);
  const handleClearMakeModels = useCallback(() => {
    // Collapse expanded makes for a cleaner UI after clearing
    setExpandedMakes([]);
    // Single navigation to delete both keys prevents param reappearing
    clearMakeModelQuery();
  }, [clearMakeModelQuery]);
  const handleMakeToggle = useCallback((make_0: string) => {
    setExpandedMakes(prev_0 => {
      if (prev_0.includes(make_0)) {
        return prev_0.filter(m => m !== make_0);
      } else {
        return [...prev_0, make_0];
      }
    });
  }, []);

  // Current filter values
  const currentValues = useMemo(() => ({
    selectedMakes: hooks.selectedMakes,
    selectedModels: hooks.selectedModels,
    selectedBodyStyles: hooks.selectedBodyStyles,
    yearRange: hooks.yearRange,
    mileageRange: hooks.mileageRange,
    priceRange: hooks.priceRange,
    selectedDriveTrains: hooks.selectedDriveTrains,
    selectedEngines: hooks.selectedEngines,
    selectedCylinders: hooks.selectedCylinders,
    selectedTransmissions: hooks.selectedTransmissions,
    selectedFuelTypes: hooks.selectedFuelTypes,
    selectedDoors: hooks.selectedDoors,
    selectedExteriorColors: hooks.selectedExteriorColors,
    selectedInteriorColors: hooks.selectedInteriorColors
  }), [hooks]);

  // Format functions
  const formatCurrency = useCallback((value: number) => {
    return "$" + value.toLocaleString();
  }, []);
  const formatNumber = useCallback((value_0: number) => {
    return value_0.toLocaleString();
  }, []);

  // Track filter changes as search refinements
  const trackFilterChange = useCallback((filterType: string, filterValue: any) => {
    if (hooks.hasActiveFilters) {
      sendGTMEvent({
        event: "search",
        search_term: hooks.searchQuery || "",
        search_type: "vehicle_inventory_filter",
        filter_type: filterType,
        filter_value: filterValue,
        active_filters: {
          makes: currentValues.selectedMakes.length,
          models: currentValues.selectedModels.length,
          body_styles: currentValues.selectedBodyStyles.length,
          price_min: currentValues.priceRange[0],
          price_max: currentValues.priceRange[1],
          year_min: currentValues.yearRange[0],
          year_max: currentValues.yearRange[1],
          mileage_min: currentValues.mileageRange[0],
          mileage_max: currentValues.mileageRange[1]
        },
        results_count: props.carsCount
      });
    }
  }, [hooks.hasActiveFilters, hooks.searchQuery, currentValues, props.carsCount]);
  return {
    hooks,
    rangeBounds,
    makesData,
    expandedMakes,
    currentValues,
    hasActiveFilters: hooks.hasActiveFilters,
    clearAllFilters: hooks.clearAllFilters,
    formatCurrency,
    formatNumber,
    handleMakeChange,
    handleModelChange,
    handleMakeModelChange,
    handleMakeToggle,
    handleClearMakes,
    handleClearModels,
    handleClearMakeModels,
    trackFilterChange
  };
};